import { useLayoutEffect, useRef, useState } from 'react';
import { ImperativePanelGroupHandle } from 'react-resizable-panels';
import { useAppSelector } from '../../../app/hooks';
import {
  selectCurrentTaskAIStatus,
  selectIsCurrentTaskUnsupervised,
} from '../../../redux/features/taskRouter/taskRouter.selector';
import { selectUserProfile } from '../../../selectors/user';
import { useLayoutPreference } from '../../Layout/LayoutPreferenceContext';
import {
  DEFAULT_PANE_SIZES,
  MAX_PANE_SIZE,
  MIN_PANE_SIZE,
} from '../../Layout/LayoutPreferenceDialog';

// Widest the left pane can go under the 25–50% per-pane clamp: left maxed, the
// center + cart at their minimum but still visible. Used for the unsupervised
// task-router arrival, where the left pane opens expanded so the transcript gets
// the room while the center menu + cart stay on screen.
export const EXPANDED_LEFT_PANE_SIZES: [number, number, number] = [
  MAX_PANE_SIZE,
  MIN_PANE_SIZE,
  MIN_PANE_SIZE,
];

/**
 * The pane layout to apply on task-router arrival, or null to leave the
 * persisted/default sizes untouched. An UNSUPERVISED AI task arrival opens with
 * the left pane expanded to 50% so the transcript gets the room while the center
 * menu + cart stay visible. A SUPERVISED arrival returns null, so the agent's own
 * saved pane preferences (PanelGroup autoSaveId in localStorage), or the
 * defaults, are kept.
 */
export const getArrivalPaneLayout = (
  currentTaskAIStatus: unknown,
  isCurrentTaskUnsupervised: boolean
): typeof EXPANDED_LEFT_PANE_SIZES | null =>
  Boolean(currentTaskAIStatus) && isCurrentTaskUnsupervised
    ? EXPANDED_LEFT_PANE_SIZES
    : null;

/**
 * Resizable-pane wiring for the 3-pane console: a per-agent storage key
 * (autoSaveId), the live sizes kept in sync for the preference dialog, the
 * imperative PanelGroup handle the dialog writes to, and the layout-preference
 * dialog open/close state (opened from the header ProfileMenu via context).
 */
const usePaneLayout = () => {
  const userProfile = useAppSelector(selectUserProfile);
  const paneStorageKey = `hitl-console-panes:${
    userProfile?.username || userProfile?.email || 'default'
  }`;
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const [liveSizes, setLiveSizes] = useState<number[]>(DEFAULT_PANE_SIZES);

  const currentTaskAIStatus = useAppSelector(selectCurrentTaskAIStatus);
  const isCurrentTaskUnsupervised = useAppSelector(
    selectIsCurrentTaskUnsupervised
  );

  // On an UNSUPERVISED task-router arrival the left pane opens expanded to 50% as
  // a real resize, so the transcript gets the room while the center menu + cart
  // stay visible. SUPERVISED arrivals keep the agent's saved pane preferences
  // (see getArrivalPaneLayout). useLayoutEffect (not useEffect) applies it in the
  // same paint the PanelGroup restores its saved sizes, so there's no flash of
  // the persisted layout first. Runs once on arrival; the agent drags the handles
  // / resets in the layout dialog after.
  useLayoutEffect(() => {
    const arrivalLayout = getArrivalPaneLayout(
      currentTaskAIStatus,
      isCurrentTaskUnsupervised
    );
    if (arrivalLayout) panelGroupRef.current?.setLayout(arrivalLayout);
    // Mount-only: both flags are set in completeTRSetup before this mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isOpen: isLayoutPrefOpen, close: closeLayoutPref } =
    useLayoutPreference();
  const applyPaneSizes = (sizes: number[]) =>
    panelGroupRef.current?.setLayout(sizes);
  const resetPaneSizes = () =>
    panelGroupRef.current?.setLayout(DEFAULT_PANE_SIZES);

  return {
    paneStorageKey,
    panelGroupRef,
    liveSizes,
    setLiveSizes,
    isLayoutPrefOpen,
    closeLayoutPref,
    applyPaneSizes,
    resetPaneSizes,
  };
};

export default usePaneLayout;
